import { pdf } from '@react-pdf/renderer'
import { saveAs } from 'file-saver'
import InvoiceTemplate from './InvoiceTemplate'
import { createDate } from '../../utils/dateUtils.js'

class InvoicePDFGenerator {
  static async generatePDF(invoiceData, farmInfo, options = {}) {
    const {
      showMiniStatement = false,
      miniStatement = [],
      invoiceNumberRange = null
    } = options

    // Validate inputs
    if (!invoiceData) {
      throw new Error('Invoice data is required to generate PDF')
    }

    if (!farmInfo) {
      throw new Error('Farm information is required to generate PDF')
    }

    if (!farmInfo.business_name) {
      throw new Error('Business name is required in farm information')
    }

    if (!invoiceData.customer || !invoiceData.customer.name) {
      throw new Error('Customer information is required in invoice data')
    }

    try {
      // Generate the PDF blob
      const blob = await pdf(
        <InvoiceTemplate
          invoiceData={invoiceData}
          farmInfo={farmInfo}
          miniStatement={miniStatement}
          showMiniStatement={showMiniStatement}
          invoiceNumberRange={invoiceNumberRange}
        />
      ).toBlob()

      if (!blob) {
        throw new Error('PDF generation returned empty result')
      }

      return blob
    } catch (error) {
      console.error('Error generating PDF:', error)
      if (error.message.includes('required')) {
        throw error // Re-throw validation errors as-is
      }
      if (error.message.includes('Not valid image extension')) {
        console.warn('Invalid image extension in logo URL, PDF will be generated without logo')
        // Try again without the problematic logo
        try {
          const fallbackFarmInfo = { ...farmInfo, logo_file_path: null }
          const blob = await pdf(
            <InvoiceTemplate 
              invoiceData={invoiceData}
              farmInfo={fallbackFarmInfo}
              miniStatement={miniStatement}
              showMiniStatement={showMiniStatement}
            />
          ).toBlob()
          return blob
        } catch (fallbackError) {
          throw new Error('Failed to generate PDF even without logo: ' + fallbackError.message)
        }
      }
      throw new Error('Failed to generate PDF: ' + error.message)
    }
  }

  static async downloadPDF(invoiceData, farmInfo, options = {}) {
    const {
      showMiniStatement = false,
      miniStatement = [],
      filename = null
    } = options

    try {
      const blob = await this.generatePDF(invoiceData, farmInfo, {
        showMiniStatement,
        miniStatement
      })

      // Generate filename if not provided
      const pdfFilename = filename || 
        `Invoice_${invoiceData.invoiceNumber}_${invoiceData.customer.name.replace(/\s+/g, '_')}.pdf`

      // Download the file
      saveAs(blob, pdfFilename)

      return { success: true, filename: pdfFilename }
    } catch (error) {
      console.error('Error downloading PDF:', error)
      throw new Error('Failed to download PDF: ' + error.message)
    }
  }

  static async uploadPDFToSupabase(invoiceData, farmInfo, supabaseClient, options = {}) {
    const {
      showMiniStatement = false,
      miniStatement = [],
      bucketName = 'invoices',
      customFileName = null,
      invoiceNumberRange = null
    } = options

    try {
      // Generate the PDF blob
      const blob = await this.generatePDF(invoiceData, farmInfo, {
        showMiniStatement,
        miniStatement,
        invoiceNumberRange
      })

      // Generate file path
      const now = createDate()
      const timestamp = now.getTime()
      const year = now.getFullYear()
      const fileName = customFileName || `${invoiceData.invoiceNumber}_${timestamp}.pdf`
      const filePath = `${year}/${fileName}`

      // Upload to Supabase storage
      const { error } = await supabaseClient.storage
        .from(bucketName)
        .upload(filePath, blob, {
          contentType: 'application/pdf',
          upsert: false
        })

      if (error) {
        throw new Error(`Supabase upload error: ${error.message}`)
      }

      // Get public URL
      const { data: urlData } = supabaseClient.storage
        .from(bucketName)
        .getPublicUrl(filePath)

      return {
        success: true,
        filePath: filePath,
        publicUrl: urlData.publicUrl,
        fileName: fileName
      }
    } catch (error) {
      console.error('Error uploading PDF to Supabase:', error)
      throw new Error('Failed to upload PDF to storage: ' + error.message)
    }
  }

  static generateMiniStatement(invoices, payments, options = {}) {
    const {
      type = 'since_zero', // 'since_zero', 'period', 'transactions'
      startDate = null,
      endDate = null,
      transactionCount = 10
    } = options

    const entries = []

    // Add invoices
    invoices.forEach(invoice => {
      if (!invoice.paid) {
        entries.push({
          date: invoice.date,
          description: `Invoice ${invoice.invoice_no}`,
          amount: parseFloat(invoice.total),
          type: 'invoice'
        })
      }
    })

    // Add payments (as negative amounts)
    payments.forEach(payment => {
      entries.push({
        date: payment.date,
        description: `Payment - ${payment.method}`,
        amount: -parseFloat(payment.amount),
        type: 'payment'
      })
    })

    // Sort by date
    entries.sort((a, b) => new Date(a.date) - new Date(b.date))

    // Filter based on type
    let filteredEntries = entries

    if (type === 'period' && startDate && endDate) {
      const start = new Date(startDate)
      const end = new Date(endDate)
      filteredEntries = entries.filter(entry => {
        const entryDate = new Date(entry.date)
        return entryDate >= start && entryDate <= end
      })
    } else if (type === 'transactions' && transactionCount) {
      filteredEntries = entries.slice(-transactionCount)
    }
    // 'since_zero' shows all entries (default)

    return filteredEntries
  }
}

export default InvoicePDFGenerator
